import {type RefObject, useEffect, useRef} from 'react'

import {isTextEntry} from '#/lib/textEntry'

const SWIPE_DISTANCE = 44
const RUBBER_BAND_EDGE = 0.32
const WHEEL_COOLDOWN_MS = 420
const WHEEL_MIN_DELTA = 14
/** How long a released page takes to ease back where it belongs. */
const RETURN_EASE_MS = 160
/** A frame or two past the ease, when its transition may be taken off the page again. */
const RETURN_CLEANUP_MS = RETURN_EASE_MS + 20

type Options = {
  /** Element that captures pointer events */
  stageRef: RefObject<HTMLElement | null>
  /** Element that follows the finger (the page) */
  pageRef: RefObject<HTMLElement | null>
  page: number
  numPages: number
  onPageChange: (page: number) => void
  enabled?: boolean
}

/**
 * Swipe, trackpad-scroll and keyboard paging for the document carousel.
 *
 * The drag is applied straight to the page element's transform instead of
 * through React state, so following a finger doesn't re-render the canvas —
 * swiping stays smooth even on long documents.
 */
export function usePagerGestures({
  stageRef,
  pageRef,
  page,
  numPages,
  onPageChange,
  enabled = true,
}: Options) {
  const stateRef = useRef({page, numPages, onPageChange})
  stateRef.current = {page, numPages, onPageChange}

  useEffect(() => {
    const stage = stageRef.current
    if (!stage || !enabled) return

    let pointerId: number | null = null
    let startX = 0
    let startY = 0
    let startTime = 0
    let dragging = false
    let lastWheelAt = 0

    /**
     * Whose return is wearing the page's transition — so only that return's own timer may
     * take it off.
     *
     * A number rather than a flag, and bumped per return (see `animateBack`): a swipe made
     * inside another's 160ms takes it and leaves the older timer with nothing to do.
     */
    let ease = 0

    const setTransform = (x: number) => {
      const el = pageRef.current
      if (!el) return
      el.style.transform = x === 0 ? '' : `translate3d(${x}px, 0, 0)`
    }

    /**
     * Let the page back where it belongs, easing.
     *
     * The transition belongs to *this* return rather than to the page element, so only its
     * own timer may clear it: a swipe made inside the 160ms — a fast double swipe, which is
     * exactly what makes the gesture feel loose on a card — takes the token, and the older
     * cleanup then finds nothing to do instead of cutting the newer ease off mid-flight.
     * That cut is the page jumping, since a page that stops easing is drawn where the
     * interpolation had got to. (The fullscreen reader's flick carries the same token,
     * `easeRef` there, for the same reason.)
     *
     * Nothing clears the transition on the way *in*: a hand that grabs the page mid-ease
     * therefore keeps the transition it caught, and the sheet eases to the hand rather than
     * jumping under it — the browser is the only thing that knows where an easing transform
     * has got to. Once the caught ease is over, the timer takes the transition off and the
     * page follows the finger one to one.
     */
    const animateBack = () => {
      const el = pageRef.current
      if (!el) return
      const worn = ++ease
      el.style.transition = `transform ${RETURN_EASE_MS}ms ease-out`
      setTransform(0)
      window.setTimeout(() => {
        if (ease !== worn) return
        // Only ever the page that was moved, in case the effect was re-run under this
        // timer and a fresh page element took the stage's place.
        if (pageRef.current !== el) return
        el.style.transition = ''
      }, RETURN_CLEANUP_MS)
    }

    const go = (delta: number) => {
      const {page: current, numPages: total} = stateRef.current
      const next = Math.min(Math.max(current + delta, 1), total)
      if (next !== current) {
        stateRef.current.onPageChange(next)
      }
    }

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0 && event.pointerType === 'mouse') return
      pointerId = event.pointerId
      startX = event.clientX
      startY = event.clientY
      startTime = Date.now()
      dragging = false
    }

    const onPointerMove = (event: PointerEvent) => {
      if (pointerId === null || event.pointerId !== pointerId) return
      const dx = event.clientX - startX
      const dy = event.clientY - startY
      if (!dragging) {
        if (Math.abs(dx) < 5 || Math.abs(dx) < Math.abs(dy)) return
        dragging = true
        try {
          stage.setPointerCapture(pointerId)
        } catch {
          // ignore, pointer capture is best effort
        }
        event.preventDefault()
      }
      const {page: current, numPages: total} = stateRef.current
      const atStart = current === 1 && dx > 0
      const atEnd = current === total && dx < 0
      setTransform(atStart || atEnd ? dx * RUBBER_BAND_EDGE : dx)
    }

    const endDrag = (event: PointerEvent) => {
      if (pointerId === null || event.pointerId !== pointerId) return
      const dx = event.clientX - startX
      const elapsed = Date.now() - startTime
      const wasDragging = dragging
      pointerId = null
      dragging = false
      try {
        stage.releasePointerCapture(event.pointerId)
      } catch {
        // ignore
      }

      if (!wasDragging) {
        // A plain tap does nothing on purpose: the carousel is a preview, and
        // the reader only opens from its explicit expand control.
        return
      }

      // Velocity-aware: a quick flick counts even if it didn't travel far
      const velocity = Math.abs(dx) / Math.max(elapsed, 1)
      const fast = velocity > 0.5
      if (Math.abs(dx) > SWIPE_DISTANCE || (fast && Math.abs(dx) > 20)) {
        if (dx < 0) {
          // Swipe left: does the current page have a next page, or do we
          // need to hand off to the next post? We just clamp.
          const {page: current, numPages: total} = stateRef.current
          if (current < total) {
            animateBack()
            go(1)
            return
          }
        } else {
          const {page: current} = stateRef.current
          if (current > 1) {
            animateBack()
            go(-1)
            return
          }
        }
      }
      animateBack()
    }

    const onPointerCancel = (event: PointerEvent) => {
      if (pointerId === null || event.pointerId !== pointerId) return
      pointerId = null
      dragging = false
      animateBack()
    }

    const onWheel = (event: WheelEvent) => {
      const {numPages: total} = stateRef.current
      if (total <= 1) return
      const now = Date.now()
      if (now - lastWheelAt < WHEEL_COOLDOWN_MS) return
      const horizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY)
      const delta = horizontal
        ? event.deltaX
        : event.shiftKey
          ? event.deltaY
          : 0
      if (Math.abs(delta) < WHEEL_MIN_DELTA) return
      lastWheelAt = now
      event.preventDefault()
      go(delta > 0 ? 1 : -1)
    }

    const onKeyDown = (event: KeyboardEvent) => {
      /*
       * Not while somebody is writing in the stage. The keys below are the ones a
       * caret needs (`Home`, `End`, the arrows), and a field that lives inside a
       * pager — an author's question over its own page — would otherwise turn the
       * page out from under the words being typed into it.
       */
      if (isTextEntry(event.target)) return
      const {page: current, numPages: total} = stateRef.current
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case 'PageDown':
          if (current >= total) return
          event.preventDefault()
          go(1)
          break
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'PageUp':
          if (current <= 1) return
          event.preventDefault()
          go(-1)
          break
        case 'Home':
          event.preventDefault()
          stateRef.current.onPageChange(1)
          break
        case 'End':
          event.preventDefault()
          stateRef.current.onPageChange(total)
          break
      }
    }

    stage.addEventListener('pointerdown', onPointerDown)
    stage.addEventListener('pointermove', onPointerMove)
    stage.addEventListener('pointerup', endDrag)
    stage.addEventListener('pointercancel', onPointerCancel)
    stage.addEventListener('wheel', onWheel, {passive: false})
    stage.addEventListener('keydown', onKeyDown)

    return () => {
      stage.removeEventListener('pointerdown', onPointerDown)
      stage.removeEventListener('pointermove', onPointerMove)
      stage.removeEventListener('pointerup', endDrag)
      stage.removeEventListener('pointercancel', onPointerCancel)
      stage.removeEventListener('wheel', onWheel)
      stage.removeEventListener('keydown', onKeyDown)
      // The teardown is a return's cleanup too: it takes the token so a timer still in
      // flight cannot clear the transition of the next effect's page.
      ease += 1
      setTransform(0)
    }
  }, [stageRef, pageRef, enabled])
}
